import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { themeFor, hexToRgba } from "@/lib/menuTheme";
import { assetUrl } from "@/lib/api";
import { CategoryIcon } from "@/components/z6/CategoryIcon";
import { setSwiping } from "@/lib/perf";

const Arrow = ({ dir, onClick, disabled }) => (
  <button type="button" data-testid={`menu-carousel-${dir}-btn`} onClick={onClick} disabled={disabled} aria-label={dir === "prev" ? "Previous menu" : "Next menu"}
    className="hidden h-12 w-12 items-center justify-center rounded-full border border-[#0B2E44]/15 bg-white/85 text-[#0B2E44] backdrop-blur-md transition-[opacity,transform,background-color] duration-200 hover:border-[#F08DB5]/60 active:scale-95 disabled:opacity-30 md:flex">
    <svg viewBox="0 0 24 24" className={`h-5 w-5 ${dir === "prev" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
  </button>
);

const Dots = ({ count, selected, onDot, glow }) => (
  <div data-testid="menu-carousel-dots" className="flex items-center sm:gap-2">
    {Array.from({ length: count }, (_, i) => (
      <button key={i} type="button" data-testid={`menu-carousel-dot-${i}`} aria-label={`Go to menu ${i + 1}`} onClick={() => onDot(i)} className="flex h-12 min-w-[38px] items-center justify-center sm:min-w-[44px]">
        <span className="block h-1.5 rounded-full transition-[width,background-color] duration-300"
          style={{ width: i === selected ? 26 : 6, background: i === selected ? "#F08DB5" : "rgba(14,59,87,0.2)" }} />
      </button>
    ))}
  </div>
);

export const MenuCarousel = ({ menus, onSelect }) => {
  const [ref, api] = useEmblaCarousel({ align: "center", containScroll: false, loop: false, dragFree: false, skipSnaps: true, dragThreshold: 6, duration: 30 });
  const [sel, setSel] = useState(0);
  const tween = useCallback(() => {
    if (!api) return;
    const progress = api.scrollProgress(), snaps = api.scrollSnapList(), n = snaps.length;
    api.slideNodes().forEach((node, i) => {
      const d = Math.max(-1.5, Math.min(1.5, (snaps[i] - progress) * (n - 1)));
      const inner = node.firstChild;
      inner.style.transform = `perspective(1100px) rotateY(${d * -12}deg) scale(${1 - Math.abs(d) * 0.07})`;
      inner.style.opacity = String(1 - Math.min(Math.abs(d), 1.2) * 0.35);
    });
  }, [api]);
  useEffect(() => {
    if (!api) return undefined;
    const onSel = () => setSel(api.selectedScrollSnap());
    const down = () => setSwiping(true);
    const settle = () => setSwiping(false);
    tween();
    onSel();
    api.on("scroll", tween).on("reInit", tween).on("select", onSel).on("pointerDown", down).on("pointerUp", settle).on("settle", settle);
    return () => api.off("scroll", tween).off("reInit", tween).off("select", onSel).off("pointerDown", down).off("pointerUp", settle).off("settle", settle);
  }, [api, tween]);
  const cards = menus.map((m) => ({ ...m, key: m.id }));
  const glow = themeFor(cards[sel]?.key).glow;
  const tapCard = (m) => onSelect(m.key);
  return (
    <div data-testid="menu-carousel" className="relative -mx-5 sm:-mx-8">
      <div className="carousel-mask overflow-hidden py-4 sm:py-6" ref={ref}>
        <div className="flex items-stretch touch-pan-y will-change-transform">
          {cards.map((m, i) => {
            const t = themeFor(m.key);
            return (
              <div key={m.key} className="min-w-0 shrink-0 grow-0 basis-[84vw] px-2 sm:basis-[46%] sm:px-2.5 lg:basis-[32%]">
                <div className="h-full will-change-transform">
                  <motion.button layoutId={`menu-shell-${m.key}`} data-testid={`menu-card-${m.key.replace(/_/g, "-")}`}
                    onClick={() => tapCard(m)} aria-current={i === sel}
                    initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + Math.min(i, 4) * 0.06, type: "spring", stiffness: 120, damping: 18 }}
                    className="relative flex h-full w-full flex-col overflow-hidden rounded-[30px] bg-white p-4 text-left sm:p-5"
                    style={{ boxShadow: `inset 0 0 0 1px ${hexToRgba(t.glow, 0.28)}, 0 30px 80px -30px ${hexToRgba(t.glow, 0.45)}` }}>
                    <div className="absolute inset-0" style={{ background: `radial-gradient(90% 70% at 70% 20%, ${hexToRgba(t.glow, 0.22)}, transparent 70%), linear-gradient(180deg, #FFFFFF, #F2FBFA)` }} />
                    <span className="absolute left-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-xl bg-white/95 p-1.5 shadow-sm ring-1 ring-[#0B2E44]/10"><CategoryIcon k={m.key} tone="light" testId={`menu-card-icon-${m.key.replace(/_/g, "-")}`} /></span>
                    <div className="relative flex aspect-[4/3] w-full items-center justify-center">
                      {m.card_photo ? (
                        <span className="block aspect-square h-full overflow-hidden rounded-full bg-white ring-1 ring-[#0B2E44]/10">
                          <img src={assetUrl(m.card_photo.url)} alt={m.short_name} width={800} height={800} draggable={false} loading="eager" decoding="async"
                            data-testid={`menu-card-photo-${m.key.replace(/_/g, "-")}`} data-fit={m.card_photo.fit}
                            className={`h-full w-full object-center ${m.card_photo.fit === "plate" ? "object-cover" : "object-contain p-[14%]"}`} />
                        </span>
                      ) : (
                        <span data-testid={`menu-card-nophoto-${m.key.replace(/_/g, "-")}`} className="aspect-square h-full rounded-full" style={{ background: "linear-gradient(150deg, #FFF1E8, #F9C3B0 60%, #BFE3DB)" }} />
                      )}
                    </div>
                    <div className="relative mt-4 px-1">
                      <p className="font-mono text-[15px] uppercase tracking-[0.3em]" style={{ color: "#B8326C" }}>{m.dish_count != null ? `${m.dish_count} dishes` : "Broths"}</p>
                      <h3 className="font-title mt-2 line-clamp-2 min-h-[2.1em] text-[clamp(22px,5.4vw,32px)] leading-[1.05] text-[#0B2E44]">{m.short_name}</h3>
                      <span aria-hidden className="mt-2 block h-px w-10 bg-[#F08DB5]/70" />
                      <p className="font-ui mt-2 line-clamp-2 text-[15px] leading-relaxed text-[#0B2E44]">{m.intro}</p>
                      <span className="font-ui mt-5 inline-flex items-center gap-2 text-[16px] font-normal" style={{ color: "#0B2E44" }}>Enter <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span>
                    </div>
                  </motion.button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-0 flex items-center justify-center gap-4 sm:mt-2 px-5 sm:justify-between sm:px-8">
        <span data-testid="menu-carousel-position" className="hidden sm:inline font-mono text-[15px] tracking-[0.25em] text-[#0B2E44]">{String(sel + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}</span>
        <Dots count={cards.length} selected={sel} onDot={(i) => api?.scrollTo(i)} glow={glow} />
        <div className="hidden gap-2 md:flex">
          <Arrow dir="prev" onClick={() => api?.scrollPrev()} disabled={sel === 0} />
          <Arrow dir="next" onClick={() => api?.scrollNext()} disabled={sel === cards.length - 1} />
        </div>
      </div>
    </div>
  );
};
